import JsonResponse from "csi/comm/JsonResponse";
import { getImageSrc } from "csi/model/image";
import { TYPE_USER } from "csi/reflection/reflectionConstants";
import { getSearch, runSearch } from "csi/service/dataStoreService";
import { C8Node } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import { MentionUser } from "designSystem/components/editor/tributeService";
import { IUseUserProvider, UserQuery } from "designSystem/user";

/**
 * Scenario:
 * when user type @ without any chars then we will trigger containerID search then cache it
 * 		- if get 0 user or it's new conversation then return nothing
 * when user type @x one char then we will try to filter x from containerID search cache
 * 		- if get 0 user or it's new conversation then return nothing
 * when user type @xx 
 * 		- 1. if containerID search cache is more than 10 users then just return ??
 *      - 2. if containerID search cache is less than 10 user or 0 user do global search and cache it
 * 			- 2.1 when cache should we combine both search from containerID search and global search
 * when user type @xxx or more we just search from cache
 * 
 * Question: should we combine global search and containerID search in server side or client side
 * 
 * @param inputText 
 * 
 * @returns 
 */
export class ConversationUserProvider implements IUseUserProvider<MentionUser> {
	private _primaryUrl: string;
	private _commentId: string;
	private _isReply: boolean;
	private _searchQuery: Search;
	private _cache: Record<string, MentionUser[]> = {};
	constructor(primaryUrl: string, commentId: string, isReply: boolean) {
		this._primaryUrl = primaryUrl;
		this._commentId = commentId;
		this._isReply = isReply;
		const search = new Search({ Module: "SiteAdmin", Operation: "SearchConversationUsers" });
		search.getQuery()
			.Type(TYPE_USER)
			.Attr("Active", xml.OP_EQUALS, true)
			.Attr("UserType", xml.OP_EQUALS, "UserType:User");
		this._searchQuery = search;
	}

	/**
	 * clear search cache
	 */
	clearSearchCache = (): void => {
		this._cache = {};
	};

	/**
	 * Implement user provider interface
	 */
	search = async (text: UserQuery): Promise<MentionUser[]> => {
		const response: JsonResponse = await getSearch("User.Available")
			.getRequest()
			.go();
		const users: MentionUser[] = this.responseToMentionUser(response);
		return users;
	};


	/**
	 * Implement search for cache
	 */
	searchWithCache = async (query: UserQuery): Promise<MentionUser[]> => {
		const queryInput: string = query.input;
		const prefixInputChar = queryInput.substring(0, 2)
			.toLowerCase();

		if (queryInput.length < 2) {
			return this.documentCommentSearch(query);
		}

		if (this._cache[prefixInputChar]) {
			return this._cache[prefixInputChar]
				.filter((user) => this._matchesNameOrEmail(user, queryInput));
		}

		const search = this._searchQuery
			.addDependencyPaths([pathCreate("Details")])
			.addParameter("URL", this._primaryUrl)
			.addParameter("Comment", this._commentId)
			.addParameter("isReply", this._isReply)
			.addParameter("SearchString", queryInput);

		const response: JsonResponse = await runSearch(search);
		let users: MentionUser[] = this.responseToMentionUser(response);
		users = this._cache[this._primaryUrl]?.concat(users);
		this._cache[prefixInputChar] = [...new Map(users.map((user) => [user.url, user]))
			.values()];
		return this._cache[prefixInputChar];
	};

	/**
	 * filter input by name and email
	 */
	private _matchesNameOrEmail = (user: MentionUser, queryInput: string): boolean => user.fullName.toLowerCase()
		.includes(queryInput.toLowerCase()) ||
		user.email?.toLowerCase()
			.includes(queryInput.toLowerCase());

	/**
	 * search document document for relevant user 
	 */
	private documentCommentSearch = async (query: UserQuery): Promise<MentionUser[]> => {
		const key: string = this._primaryUrl;
		const queryInput: string = query.input;

		if (this._cache[key]) {
			return this._cache[key]
				.filter((user) => this._matchesNameOrEmail(user, queryInput));
		}

		const search = this._searchQuery
			.addDependencyPaths([pathCreate("Details")])
			.addParameter("URL", this._primaryUrl)
			.addParameter("Comment", this._commentId)
			.addParameter("isReply", this._isReply);
		const response: JsonResponse = await runSearch(search);
		const users: MentionUser[] = this.responseToMentionUser(response);
		this._cache[key] = users;
		return users;
	};

	/**
	 * map node response to mention user type 
	 */
	private responseToMentionUser = (response: JsonResponse): MentionUser[] => {
		return response.getResultNodes()
			.map<MentionUser>((node: C8Node, index: number) => {
				const email = response.getNode(node?.Details)?.Email;
				return {
					url: node.$URL,
					firstName: node.FirstName,
					lastName: node.LastName,
					fullName: node.$Name,
					avatar: getImageSrc(node),
					email: email,
					lookup: node.$Name + " " + email,
				} as MentionUser;
			});
	};
}